import axios from "@/axios";

export default {
  state: {
    generativeTextsDatasets: [],
    generativeTextsLanguages: [],
    generativeSettingProfiles: [],
    generativeDatamodels: [],
    generateConfiguratorDialogVisible: false,
    replaceDictionaries: [],
    selectedSettingProfile: null,
    datasetFolders: [],
    profileFolders: [],
    currentDatasetFolderId: null,
    currentProfileFolderId: null,
    datasetBreadcrumb: [],
    profileBreadcrumb: [],
  },
  getters: {
    getFolders: (state) => state.datasetFolders,
    getProfileFolders: (state) => state.profileFolders,
    getCurrentDatasetFolderId: (state) => state.currentDatasetFolderId,
    getCurrentProfileFolderId: (state) => state.currentProfileFolderId,
    getDatasetBreadcrumb: (state) => state.datasetBreadcrumb,
    getProfileBreadcrumb: (state) => state.profileBreadcrumb,
    getGenerativeTextsDatasets: (state) => state.generativeTextsDatasets,
  },
  mutations: {
    setGenerateConfiguratorDialogVisible(state, visible) {
      state.generateConfiguratorDialogVisible = visible;
    },
    updateGenerativeTextsDatasets(state, datasets) {
      state.generativeTextsDatasets = datasets;
    },
    setGenerativeTextsLanguages(state, data) {
      state.generativeTextsLanguages = data;
    },
    setGenerativeTextsDatamodels(state, data) {
      state.generativeDatamodels = data;
    },
    setGenerativeSettingProfiles(state, data) {
      state.generativeSettingProfiles = data;
    },
    setReplacementDictionaries(state, data) {
      state.replaceDictionaries = data;
    },
    setSelectedSettingProfile(state, item) {
      state.selectedSettingProfile = item;
    },
    setFolders(state, folders) {
      state.datasetFolders = folders;
    },
    setProfileFolders(state, folders) {
      state.profileFolders = folders;
    },
    setCurrentDatasetFolderId(state, folderId) {
      state.currentDatasetFolderId = folderId;
    },
    setCurrentProfileFolderId(state, folderId) {
      state.currentProfileFolderId = folderId;
    },
    setDatasetBreadcrumb(state, breadcrumb) {
      state.datasetBreadcrumb = breadcrumb;
    },
    setProfileBreadcrumb(state, breadcrumb) {
      state.profileBreadcrumb = breadcrumb;
    },
    addFolder(state, folder) {
      state.datasetFolders.push(folder);
    },
    updateFolder(state, updatedFolder) {
      const index = state.datasetFolders.findIndex(
        (f) => f.id === updatedFolder.id,
      );
      if (index !== -1) {
        state.datasetFolders.splice(index, 1, updatedFolder);
      }
    },
    removeFolder(state, folderId) {
      state.datasetFolders = state.datasetFolders.filter(
        (f) => f.id !== folderId,
      );
    },
  },
  actions: {
    getGenerativeTextsDatasets({ commit }, folderId = null) {
      const params = folderId !== null ? { folder_id: folderId } : {};
      return axios
        .get(`/api/generative-texts/datasets`, { params })
        .then((response) => {
          commit("updateGenerativeTextsDatasets", response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch generative texts datasets:', error);
          return Promise.reject(error);
        });
    },
    getGenerativeTextsLanguages({ commit }) {
      axios
        .get(`/api/generative-texts/languages`)
        .then((response) => {
          commit("setGenerativeTextsLanguages", response.data);
        })
        .catch(() => { });
    },
    getGenerativeTextsDatamodels({ commit }) {
      axios
        .get(`/api/generative-texts/datamodels`)
        .then((response) => {
          commit("setGenerativeTextsDatamodels", response.data);
        })
        .catch(() => { });
    },
    getGenerativeSettingProfiles({ commit }, folderId = null) {
      const params = folderId !== null ? { folder_id: folderId } : {};
      return axios
        .get(`/api/generative-texts/settings`, { params })
        .then((response) => {
          commit("setGenerativeSettingProfiles", response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getAllGenerativeSettingProfiles({ commit }) {
      return axios
        .get(`/api/generative-texts/settings/all`)
        .then((response) => {
          commit("setGenerativeSettingProfiles", response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getAllGenerativeTextsDatasets({ commit }) {
      return axios
        .get(`/api/generative-texts/datasets/all`)
        .then((response) => {
          commit("updateGenerativeTextsDatasets", response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch all datasets:', error);
          return Promise.reject(error);
        });
    },
    fetchFolders({ commit }, parentFolderId = null) {
      const params = {
        module_type: "generative_texts",
        ...(parentFolderId !== null && { parent_folder_id: parentFolderId }),
      };
      return axios
        .get("/api/folders", { params })
        .then((response) => {
          commit("setFolders", response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch generative texts folders:', error);
          return Promise.reject(error);
        });
    },
    fetchProfileFolders({ commit }, parentFolderId = null) {
      const params = {
        module_type: "generative_texts_profile",
        ...(parentFolderId !== null && { parent_folder_id: parentFolderId }),
      };
      return axios
        .get("/api/folders", { params })
        .then((response) => {
          commit("setProfileFolders", response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch profile folders:', error);
          return Promise.reject(error);
        });
    },
    fetchFoldersForDialog({ commit }, parentFolderId = null) {
      const params = {
        module_type: "generative_texts",
        ...(parentFolderId !== null && { parent_folder_id: parentFolderId }),
      };
      return axios
        .get("/api/folders", { params })
        .then((response) => response.data)
        .catch((error) => {
          throw error;
        });
    },
    fetchBreadcrumb({ commit }, folderId) {
      if (folderId === null) {
        commit("setDatasetBreadcrumb", []);
        return Promise.resolve();
      }
      return axios
        .get(`/api/folders/${folderId}/breadcrumb`, {
          params: { module_type: "generative_texts" },
        })
        .then((response) => {
          commit("setDatasetBreadcrumb", response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch dataset breadcrumb:', error);
          return Promise.reject(error);
        });
    },
    fetchBreadcrumbForDialog({ commit }, folderId) {
      if (folderId === null) {
        return Promise.resolve([]);
      }
      return axios
        .get(`/api/folders/${folderId}/breadcrumb`, {
          params: { module_type: "generative_texts" },
        })
        .then((response) => response.data)
        .catch((error) => {
          throw error;
        });
    },
    fetchProfileBreadcrumb({ commit }, folderId) {
      if (folderId === null) {
        commit("setProfileBreadcrumb", []);
        return Promise.resolve();
      }
      return axios
        .get(`/api/folders/${folderId}/breadcrumb`, {
          params: { module_type: "generative_texts_profile" },
        })
        .then((response) => {
          commit("setProfileBreadcrumb", response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch profile breadcrumb:', error);
          return Promise.reject(error);
        });
    },
    fetchProfileBreadcrumbForDialog({ commit }, folderId) {
      if (folderId === null) {
        return Promise.resolve([]);
      }
      return axios
        .get(`/api/folders/${folderId}/breadcrumb`, {
          params: { module_type: "generative_texts_profile" },
        })
        .then((response) => response.data)
        .catch((error) => {
          throw error;
        });
    },
    createFolder({ commit }, { name, parent_folder_id = null }) {
      return axios
        .post(
          "/api/folders",
          {
            name,
            parent_folder_id,
          },
          {
            params: { module_type: "generative_texts" },
          },
        )
        .then((response) => {
          commit("addFolder", response.data);
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    createProfileFolder({ commit }, { name, parent_folder_id = null }) {
      return axios
        .post(
          "/api/folders",
          {
            name,
            parent_folder_id,
          },
          {
            params: { module_type: "generative_texts_profile" },
          },
        )
        .then((response) => {
          commit("addFolder", response.data);
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    renameFolder(
      { commit },
      { folderId, name, module_type = "generative_texts" },
    ) {
      return axios
        .put(
          `/api/folders/${folderId}`,
          { name },
          {
            params: { module_type },
          },
        )
        .then((response) => {
          commit("updateFolder", response.data);
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    deleteFolder({ commit }, { folderId, module_type = "generative_texts" }) {
      return axios
        .delete(`/api/folders/${folderId}`, {
          params: { module_type },
        })
        .then((response) => {
          commit("removeFolder", folderId);
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    moveFolder(
      { commit },
      { folderId, parent_folder_id, module_type = "generative_texts" },
    ) {
      return axios
        .put(
          `/api/folders/${folderId}/move`,
          {
            parent_folder_id: parent_folder_id,
          },
          {
            params: { module_type },
          },
        )
        .then((response) => {
          commit("updateFolder", response.data);
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    moveDataset({ dispatch }, { datasetId, folder_id }) {
      return axios
        .put(`/api/generative-texts/datasets/${datasetId}/move`, {
          folder_id,
        })
        .then((response) => {
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    moveProfile({ dispatch }, { profileId, folder_id, profileData }) {
      // Update profile with new folder_id while preserving other fields
      const updatedProfile = {
        ...profileData,
        folder_id,
      };
      return axios
        .put(`/api/generative-texts/settings/${profileId}`, updatedProfile)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          throw error;
        });
    },
    fetchProfileById({ commit }, profileId) {
      return axios
        .get(`/api/generative-texts/settings/${profileId}`)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to fetch profile:", error);
          throw error;
        });
    },
    fetchDatamodelFeatures({ commit }, datamodelId) {
      return axios
        .get(`/api/generative-texts/datamodels/${datamodelId}/features`)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to fetch datamodel features:", error);
          throw error;
        });
    },
    updateProfile({ commit }, { profileId, profileData }) {
      return axios
        .put(`/api/generative-texts/settings/${profileId}`, profileData)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.error("Failed to update profile:", error);
          throw error;
        });
    },
    fetchProfileVersions({ commit }, profileId) {
      return axios
        .get(`/api/generative-texts/settings/${profileId}/versions`)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to fetch profile versions:", error);
          throw error;
        });
    },
    saveProfileVersion({ commit }, profileId) {
      return axios
        .post(`/api/generative-texts/settings/${profileId}/versions`)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to save profile version:", error);
          throw error;
        });
    },
    fetchProfileVersionGenerations({ commit }, { profileId, versionId }) {
      return axios
        .get(
          `/api/generative-texts/settings/${profileId}/versions/${versionId}/generations`,
        )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to fetch version generations:", error);
          throw error;
        });
    },
    renameProfileVersion({ commit }, { profileId, versionId, label }) {
      return axios
        .put(
          `/api/generative-texts/settings/${profileId}/versions/${versionId}`,
          { label },
        )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to rename profile version:", error);
          throw error;
        });
    },
    deleteProfileVersion({ commit }, { profileId, versionId }) {
      return axios
        .delete(
          `/api/generative-texts/settings/${profileId}/versions/${versionId}`,
        )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to delete profile version:", error);
          throw error;
        });
    },
    restoreProfileVersion({ commit }, { profileId, versionId }) {
      return axios
        .post(
          `/api/generative-texts/settings/${profileId}/versions/${versionId}/restore`,
        )
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.error("Failed to restore profile version:", error);
          throw error;
        });
    },
    createProfile({ commit, state }, profileData) {
      return axios
        .post("/api/generative-texts/settings", profileData)
        .then((response) => {
          const newProfile = response.data;
          commit("setGenerativeSettingProfiles", [
            ...state.generativeSettingProfiles,
            newProfile,
          ]);
          return newProfile;
        })
        .catch((error) => {
          console.error("Failed to create profile:", error);
          throw error;
        });
    },
    deleteProfile({ commit }, profileId) {
      return axios
        .delete(`/api/generative-texts/settings/${profileId}`)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          console.error("Failed to delete profile:", error);
          throw error;
        });
    },
    duplicateProfile({ commit, state }, profileId) {
      return axios
        .post(`/api/generative-texts/settings/${profileId}/duplicate`)
        .then((response) => {
          const newProfile = response.data;
          commit("setGenerativeSettingProfiles", [
            ...state.generativeSettingProfiles,
            newProfile,
          ]);
          return newProfile;
        })
        .catch((error) => {
          console.error("Failed to duplicate profile:", error);
          throw error;
        });
    },
    navigateToFolder({ commit, dispatch }, folderId) {
      commit("setCurrentDatasetFolderId", folderId);
      return Promise.all([
        dispatch("fetchFolders", folderId),
        dispatch("fetchBreadcrumb", folderId),
        dispatch("getGenerativeTextsDatasets", folderId)
      ]);
    },
    navigateToProfileFolder({ commit, dispatch }, folderId) {
      commit("setCurrentProfileFolderId", folderId);
      return Promise.all([
        dispatch("fetchProfileFolders", folderId),
        dispatch("fetchProfileBreadcrumb", folderId),
        dispatch("getGenerativeSettingProfiles", folderId)
      ]);
    },
  },
};
